import type { SupportContent } from "./types";

export const supportEn: SupportContent = {
  locale: "en",
  seo: {
    title: "Support - Prismark",
    description:
      "Support page for Prismark, a lightweight pro camera app for iOS."
  },
  nav: {
    home: "Prismark",
    support: "Support",
    privacy: "Privacy Policy"
  },
  hero: {
    eyebrow: "Prismark Support",
    title: "How can we help?",
    description:
      "Find answers to common questions about Prismark, including camera permissions, photo library access, dual-camera recording, and how to contact us."
  },
  guide: {
    title: "Support information",
    body: [
      "Prismark is a lightweight pro camera app for iOS, designed for intuitive manual controls and clean shooting workflows.",
      "If you encounter an issue, please check the FAQ below first. For additional help, contact us by email and include your device model, iOS version, app version, and a short description of the problem."
    ]
  },
  faq: {
    title: "Frequently asked questions",
    items: [
      {
        question: "Why does Prismark ask for camera permission?",
        answer:
          "Prismark needs camera access to let you capture photos, record videos, and use camera-related features such as dual-camera recording on supported devices."
      },
      {
        question: "Why does Prismark ask for microphone permission?",
        answer:
          "Microphone access is required when recording videos with audio. Prismark does not use the microphone when you are not recording video."
      },
      {
        question: "Why does Prismark ask for photo library access?",
        answer:
          "Photo library access is used to save your captured photos and videos, and to let you review media inside the app when needed."
      },
      {
        question: "Does Prismark upload my photos or videos?",
        answer:
          "Prismark is designed to process photos and videos on your device. The current version does not upload your photos or videos to our servers."
      },
      {
        question: "Does Prismark collect personal data?",
        answer:
          "Prismark does not require an account for its core camera features. Please review the Privacy Policy for the latest details about data handling."
      },
      {
        question: "Why is dual-camera recording not available on my device?",
        answer:
          "Two factors apply. Dual-camera recording is part of Prismark Pro, an optional one-time in-app purchase. It also requires hardware and iOS support; certain device models or lens combinations cannot run two video streams at once."
      },
      {
        question: "What does Prismark Pro include, and is it a subscription?",
        answer:
          "Prismark Pro unlocks three features: importing your own LUTs, ProRAW and RAW output, and dual-camera picture-in-picture recording. Nothing else in Prismark costs extra. It is a one-time purchase, not a subscription; there are no recurring charges and nothing to cancel."
      },
      {
        question: "How do I import a LUT, and which files work?",
        answer:
          "LUT import is part of Prismark Pro. Once unlocked, import from Settings > LUTs > Import LUT, or from the \"+\" cell at the end of the filter strip in the viewfinder. Prismark reads .cube files and Hald PNG images. You can also send a .cube file to Prismark from the Files app with \"Open in\". Imported LUTs sit next to the built-in filters in the strip, with the same intensity slider, and Settings > LUTs lets you rename, reorder, or delete them. Prismark does not ship any LUTs of its own, so the list is empty until you add one."
      },
      {
        question: "I bought Prismark Pro but ProRAW is missing. Why?",
        answer:
          "ProRAW depends on your hardware as well as the purchase. Apple only supports ProRAW on certain iPhone models; where it is unavailable, Prismark hides the section entirely rather than showing an option that cannot work, so Settings > Photo Format will not list it. LUT import and dual-camera recording are separate and are unaffected. If your device does support ProRAW and you still cannot find it, email us with your device model."
      },
      {
        question: "I bought Prismark Pro but a new device shows it as locked. What do I do?",
        answer:
          "Open Settings inside Prismark, tap the Prismark Pro entry at the top, and choose \"Restore Purchases\". Make sure the device is signed in to the same Apple Account you used to buy it. If it still does not unlock, email us with the Apple Account region and the approximate purchase date."
      },
      {
        question: "Why can recording quality or performance vary?",
        answer:
          "Camera performance can be affected by device model, iOS version, available storage, battery level, temperature, and the selected camera mode."
      },
      {
        question: "How do I report a bug or request a feature?",
        answer:
          "Please contact us by email and include your device model, iOS version, app version, steps to reproduce the issue, and screenshots or screen recordings if available."
      }
    ]
  },
  contact: {
    title: "Contact us",
    description: "For support, bug reports, feedback, or privacy-related questions, please email us.",
    emailLabel: "Email",
    email: "axqora@gmail.com",
    emailSubject: "Prismark Support Request"
  },
  relatedLinks: {
    title: "Useful links",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Use", href: "/terms" }
    ]
  }
};
